<script setup lang="ts">
import { IconMapPinFill } from "@packages/icon-library";
import { IPlaceInfo, usePlaceStore } from "@/modules/place-module";
import { computed, ComputedRef, onBeforeMount, ref } from "vue";
import { UiTextButton } from "@packages/component-library";
import {
  YandexMapMarker,
  YandexMap,
  YandexMapDefaultSchemeLayer,
  YandexMapDefaultFeaturesLayer,
} from "vue-yandex-maps";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const placeStore = usePlaceStore();
const place: ComputedRef<null | IPlaceInfo> = computed(() => placeStore.place);
const showMap = ref(true);
const mapCenter = ref([0, 0]);

onBeforeMount(() => {
  if (place.value) {
    mapCenter.value[0] = place.value.place.position.lng;
    mapCenter.value[1] = place.value.place.position.lat;
  }
});
</script>

<template>
  <section class="place-address">
    <IconMapPinFill />

    <div class="place-address__info">
      <h4>{{ t("place.address.title") }}</h4>
      <p>
        {{ place?.place.address }}
        <span v-if="place?.place.address_comment">
          {{ place?.place.address_comment }}
        </span>
      </p>
      <UiTextButton
        :label="
          showMap ? t('place.address.hideMap') : t('place.address.showMap')
        "
        size="m"
        @click="showMap = !showMap"
      />
      <div v-if="showMap" class="place-address__map">
        <YandexMap
          :settings="{
            location: {
              center: mapCenter,
              zoom: 14,
            },
          }"
        >
          <YandexMapDefaultSchemeLayer />
          <YandexMapDefaultFeaturesLayer />
          <YandexMapMarker
            :settings="{
              coordinates: mapCenter,
            }"
          >
            <div
              class="marker"
              :class="[
                `${place?.place?.badges.includes('top') ? 'marker_top' : `marker_${place?.place?.icon_code}`}`,
              ]"
            />
          </YandexMapMarker>
        </YandexMap>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.place-address {
  display: grid;
  grid-template-columns: 24px 1fr;
  gap: calc($base-space * 2);
  align-items: start;

  @media (max-width: 1023px) {
    grid-template-columns: 20px 1fr;
  }

  > .icon {
    background-color: var(--color-semantic-content-normal-contrast-secondary);

    @media (max-width: 1023px) {
      width: 20px;
      height: 20px;
    }
  }

  &__info {
    > h4 {
      @include body-bold-l();
      color: var(--color-semantic-content-normal-primary);
    }

    > p {
      margin: calc($base-space * 3) 0 0;
      display: flex;
      flex-flow: column;
      justify-content: flex-start;
      align-items: flex-start;
      gap: calc($base-space * 2);

      @media (max-width: 1023px) {
        margin: calc($base-space * 2) 0 0;
      }

      @include body-m();
      color: var(--color-semantic-content-normal-primary);

      > span {
        @include body-s();
        color: var(--color-semantic-content-normal-tertiary);
      }
    }

    > button {
      margin: calc($base-space * 2) 0 0;
    }
  }

  &__map {
    margin: calc($base-space * 4) 0 0;
    width: 100%;
    height: 216px;

    > .yandex-container {
      width: 100%;
      height: 100%;
    }
  }
}
</style>
